import * as React from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Users, UserPlus, FileText, TrendingUp, Stethoscope, Upload, Wifi, WifiOff, Loader2, Calendar } from 'lucide-react'
import { Header } from '@/components/layout/header'
import { StatCard } from '@/components/dashboard/stat-card'
import { RevenueChart } from '@/components/dashboard/revenue-chart'
import { RecentPatients } from '@/components/dashboard/recent-patients'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useHealth, useDashboardStats } from '@/hooks'
import { useBranch } from '@/providers/branch-provider'
import { formatNumber, formatCurrency } from '@/lib/utils'

export const Route = createFileRoute('/')({
  component: Dashboard,
})

type PeriodType = 'current' | 'last'

// Helper to get year/month based on period
function getPeriodDates(period: PeriodType): { year: number; month: number; label: string } {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1 // 1-indexed

  switch (period) {
    case 'current':
      return { year: currentYear, month: currentMonth, label: 'This Month' }
    case 'last': {
      // Previous month
      const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1
      const lastYear = currentMonth === 1 ? currentYear - 1 : currentYear
      return { year: lastYear, month: lastMonth, label: 'Last Month' }
    }
    default:
      return { year: currentYear, month: currentMonth, label: 'This Month' }
  }
}

function Dashboard() {
  // Period state - default to 'current' (This Month = January 2026)
  const [period, setPeriod] = React.useState<PeriodType>('current')
  const periodDates = getPeriodDates(period)

  // Get selected branch from context
  const { branchId, selectedBranch } = useBranch()

  // Fetch dashboard stats for selected period and branch
  const { data: stats, isPending: isLoadingStats, isError: isStatsError } = useDashboardStats({
    branchId,
    year: periodDates.year,
    month: periodDates.month,
  })
  const { data: health, isPending: isLoadingHealth } = useHealth()

  // Format large numbers
  const formatLargeNumber = (num: number) => {
    if (num >= 1000000) return `RM ${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `RM ${(num / 1000).toFixed(0)}K`
    return formatCurrency(num)
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-7">
      {/* Header with API Status */}
      <Header
        title="Hello, Admin"
        subtitle={stats?.period ? `${stats.period.monthName} ${stats.period.year} - ${selectedBranch.name}` : 'Loading...'}
      >
        {/* API Status Indicator */}
        <div className="flex items-center gap-2 text-sm">
          {isLoadingHealth ? (
            <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
          ) : health?.status === 'healthy' ? (
            <div className="flex items-center gap-1.5 text-success">
              <Wifi className="w-4 h-4" />
              <span className="text-xs font-medium">Live</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-warning">
              <WifiOff className="w-4 h-4" />
              <span className="text-xs font-medium">Offline</span>
            </div>
          )}
        </div>
      </Header>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-9 pb-6 lg:pb-9 flex flex-col gap-5 lg:gap-7 overflow-x-hidden">
        {/* Period Selector + Badge */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          {/* Toggle Group for Period Selection */}
          <ToggleGroup
            type="single"
            value={period}
            onValueChange={(value) => value && setPeriod(value as PeriodType)}
            className="bg-card border border-input rounded-lg p-1"
          >
            <ToggleGroupItem
              value="current"
              className="px-3 py-1.5 text-xs font-medium rounded-md data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
            >
              This Month
            </ToggleGroupItem>
            <ToggleGroupItem
              value="last"
              className="px-3 py-1.5 text-xs font-medium rounded-md data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
            >
              Last Month
            </ToggleGroupItem>
          </ToggleGroup>

          {/* Period Info */}
          {stats?.period && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-text-muted hidden sm:block" />
              <span className="text-xs sm:text-sm text-text-secondary">
                <span className="hidden sm:inline">Showing data for </span>
                <span className="font-semibold text-text-dark">{stats.period.monthName} {stats.period.year}</span>
                {branchId !== 'all' && <span className="hidden sm:inline"> - <span className="font-semibold text-text-dark">{selectedBranch.name}</span></span>}
              </span>
            </div>
          )}
        </div>

        {/* Stats Grid - Real data from API */}
        <div className="grid gap-3 sm:gap-4.5 grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Existing Patients"
            value={isLoadingStats ? '...' : isStatsError ? 'Error' : formatNumber(stats?.stats.existingPatients ?? 0)}
            subtitle={stats?.stats.existingRevenue ? formatLargeNumber(stats.stats.existingRevenue) : undefined}
            icon={Users}
            delay={0}
          />
          <StatCard
            title="New Patients"
            value={isLoadingStats ? '...' : isStatsError ? 'Error' : formatNumber(stats?.stats.newPatients ?? 0)}
            subtitle={stats?.stats.newRevenue ? formatLargeNumber(stats.stats.newRevenue) : undefined}
            icon={UserPlus}
            delay={0.1}
          />
          <StatCard
            title="Invoices"
            value={isLoadingStats ? '...' : isStatsError ? 'Error' : formatNumber(stats?.stats.totalInvoices ?? 0)}
            subtitle="This month"
            icon={FileText}
            delay={0.2}
          />
          <StatCard
            title="Revenue"
            value={isLoadingStats ? '...' : isStatsError ? 'Error' : formatLargeNumber(stats?.stats.totalRevenue ?? 0)}
            subtitle="This month"
            icon={TrendingUp}
            delay={0.3}
          />
        </div>

        {/* Content Row - Chart + Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 lg:gap-6">
          {/* Left Column - Chart and Table */}
          <div className="flex flex-col gap-4 lg:gap-6">
            {/* Revenue Chart */}
            <RevenueChart />

            {/* Recent Patients Table - Hide on mobile */}
            <div className="hidden sm:block">
              <RecentPatients />
            </div>
          </div>

          {/* Right Column - Quick Actions + Branch Stats */}
          <div className="flex flex-col gap-3 lg:gap-4.5">
            {/* Quick Actions Grid */}
            <div className="grid grid-cols-4 lg:grid-cols-2 gap-2 lg:gap-3.5">
              <QuickActionCard icon={FileText} label="View Invoice" href="/invoices" />
              <QuickActionCard icon={Users} label="View Patient" href="/patients" />
              <QuickActionCard icon={Stethoscope} label="View Treatment" href="/treatments" />
              <QuickActionCard icon={Upload} label="Upload Data" href="/upload" />
            </div>

            {/* Branch Stats Card - Hide on mobile */}
            <div className="hidden lg:block">
              <BranchStatsCard />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Quick Action Card Component
function QuickActionCard({ icon: Icon, label, href }: { icon: React.ElementType; label: string; href: string }) {
  return (
    <Link to={href}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="bg-card hover:bg-hover rounded-lg p-3 lg:p-6 flex flex-col items-center gap-2 lg:gap-3.5 cursor-pointer transition-colors duration-200 group"
      >
        <div className="w-10 h-10 lg:w-[52px] lg:h-[52px] rounded-full bg-icon-beige flex items-center justify-center transition-all duration-200 group-hover:bg-card group-hover:shadow-md">
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-foreground" />
        </div>
        <span className="text-[11px] lg:text-[13px] font-medium text-text-primary text-center leading-tight">{label}</span>
      </motion.div>
    </Link>
  )
}

// Branch Stats Card Component
function BranchStatsCard() {
  const { branchId } = useBranch()
  const { data: stats, isPending } = useDashboardStats({
    branchId,
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  })

  const branches = stats?.branchSummary ?? []

  return (
    <div className="bg-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <span className="text-[15px] font-semibold text-text-dark">Branches</span>
        <Link to="/analytics" className="text-sm font-medium text-text-secondary cursor-pointer hover:text-text-dark transition-colors">
          See all
        </Link>
      </div>
      <div className="flex flex-col gap-4.5">
        {isPending ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
          </div>
        ) : branches.map((branch, index) => (
          <div key={branch.id}>
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-dark to-info-light flex items-center justify-center text-white font-bold text-sm">
                {branch.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-text-dark mb-0.5">{branch.name}</div>
                <div className="text-[13px] text-text-muted truncate">
                  {branch.patients.toLocaleString()} patients
                </div>
              </div>
              <span className="px-3 py-1.5 rounded-full text-xs font-semibold border border-success text-success">
                Active
              </span>
            </div>
            {index < branches.length - 1 && (
              <div className="h-px bg-border mt-4" />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
